import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { supabase } from '../supabaseClient';
import './BoostPage.css';
import './BoostCheckoutPage.css';

const PACKAGES = {
  boost_single: { price: '€10.99', totalAmount: 10.99, minCount: 1, maxCount: 1 },
};

// Single-listing boost is now a static Stripe Payment Link. No server-side
// Checkout Session needed — the webhook applies the boost based on the
// configured Stripe product (no listing/user metadata flows through).
const BOOST_SINGLE_PAYMENT_LINK = 'https://buy.stripe.com/9B614n7awdc9gO08TM1kA0G';

const PACKAGE_KEY = 'boost_pkg';
const SELECTION_KEY = 'boost_selection';

function readStoredPackage() {
  try {
    const v = sessionStorage.getItem(PACKAGE_KEY);
    if (v && PACKAGES[v]) return v;
  } catch (_) {/* ignore */}
  return null;
}

function readStoredSelection() {
  try {
    const raw = sessionStorage.getItem(SELECTION_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch (_) {
    return [];
  }
}

function countMatchesPackage(pkgKey, count) {
  const pkg = PACKAGES[pkgKey];
  if (!pkg) return false;
  return count >= pkg.minCount && count <= pkg.maxCount;
}

export default function BoostCheckoutPage() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  // Source of truth: route state → sessionStorage. Both survive within a session;
  // route state covers normal nav, sessionStorage covers refresh / Stripe return.
  const packageKey = useMemo(() => {
    const fromState = location.state?.package;
    if (fromState && PACKAGES[fromState]) return fromState;
    return readStoredPackage();
  }, [location.state]);

  const listingIds = useMemo(() => {
    if (Array.isArray(location.state?.listingIds) && location.state.listingIds.length > 0) {
      return location.state.listingIds.map(String);
    }
    return readStoredSelection();
  }, [location.state]);

  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);

  // Guard: if we don't have a valid (package, ids) combo, bounce back to the right step.
  useEffect(() => {
    if (!packageKey) {
      navigate('/boost', { replace: true });
      return;
    }
    if (!listingIds.length) {
      navigate('/boost-select?package=' + packageKey, { replace: true });
      return;
    }
    if (!countMatchesPackage(packageKey, listingIds.length)) {
      // listingIds.length doesn't fit the package — send them back to the picker
      // for that package so they can correct it.
      navigate('/boost-select?package=' + packageKey, { replace: true });
    }
  }, [packageKey, listingIds, navigate]);

  // Fetch the user's listings so we can render the summary rows.
  useEffect(() => {
    if (!user || !user.id) return;
    let cancelled = false;
    setLoading(true);
    supabase
      .from('listings')
      .select('id, title, city, type, price, images, is_boosted, boost_expires_at')
      .eq('user_id', user.id)
      .in('id', listingIds)
      .then(({ data }) => {
        if (cancelled) return;
        setListings(data || []);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [user?.id, listingIds]);

  if (!packageKey || !listingIds.length) {
    // The guard effect above will redirect; render nothing in the meantime.
    return null;
  }

  const pkg = PACKAGES[packageKey];
  const pkgLabel = t('boostPage.packageListingLabel');
  const bundleLabel = `${pkgLabel} · ${t('boostPage.packageDuration')}`;

  const handleProceed = () => {
    if (redirecting) return;
    if (!user) {
      navigate(`/login?redirect=${encodeURIComponent('/boost-checkout')}`);
      return;
    }
    setRedirecting(true);
    window.location.href = BOOST_SINGLE_PAYMENT_LINK;
  };

  return (
    <div className="boost-checkout">
      <div className="boost-checkout__inner">
        <button className="boost-checkout__back" onClick={() => navigate('/boost-select?package=' + packageKey)}>
          {t('boostConfirm.back')}
        </button>

        <header className="boost-checkout__header">
          <h1 className="boost-checkout__title">{t('boostConfirm.title')}</h1>
          <p className="boost-checkout__sub">
            {t('boostConfirm.sub')}
          </p>
        </header>

        <section className="boost-checkout__section">
          <div className="boost-checkout__sectionLabel">{t('boostConfirm.sectionPackage')}</div>
          <div className="boostPackages__grid boost-checkout__pkgGrid boost-checkout__pkgGrid--single">
            <div className="boostPackages__card boostPackages__card--selected">
              <div className="boostPackages__label">{pkgLabel}</div>
              <div className="boostPackages__priceBlock">
                <div className="boostPackages__price">{pkg.price}</div>
              </div>
            </div>
          </div>
        </section>

        <section className="boost-checkout__section">
          <div className="boost-checkout__sectionLabel">{t('boostConfirm.sectionListings')} ({listingIds.length})</div>
          {loading ? (
            <div className="boost-checkout__loading">{t('boostConfirm.loading')}</div>
          ) : (
            <div className="boost-checkout__listings">
              {listings.map(l => {
                const thumb = (l.images && l.images[0]) || null;
                return (
                  <div key={l.id} className="boost-checkout__listing-row">
                    <div className="boost-checkout__listing-thumb">
                      {thumb
                        ? <img src={thumb} alt="" />
                        : <span className="boost-checkout__listing-placeholder" />}
                    </div>
                    <div className="boost-checkout__listing-meta">
                      <div className="boost-checkout__listing-title">{l.title || t('boostConfirm.untitled')}</div>
                      <div className="boost-checkout__listing-sub">
                        {[l.city, l.type].filter(Boolean).join(' · ')}
                      </div>
                    </div>
                    <div className="boost-checkout__listing-price">
                      €{Number(l.price || 0).toLocaleString()}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <section className="boost-checkout__totalRow">
          <div>
            <div className="boost-checkout__totalLabel">{t('boostConfirm.total')}</div>
            <div className="boost-checkout__totalSub">{bundleLabel}</div>
          </div>
          <div className="boost-checkout__totalAmount">{pkg.price}</div>
        </section>

        <button
          type="button"
          className="boost-checkout__cta"
          onClick={handleProceed}
          disabled={redirecting}
        >
          {redirecting ? t('boostConfirm.redirecting') : t('boostConfirm.proceed')}
        </button>

        <p className="boost-checkout__note">
          {t('boostConfirm.note')}
        </p>
      </div>
    </div>
  );
}
